import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BaseEditableTable } from '@/components/table/BaseEditableTable';
import type { EditableCellConfig } from '@/components/table/types';
import type { billing_OpenInvoiceResponse } from '@/api/models/billing_OpenInvoiceResponse';
import { InvoiceLink } from '@/components/shared/entity-links';
import { formatMoneyCents } from '@/lib/money';
import { formatDateDisplay } from '@/lib/format';

interface Props {
  invoices: billing_OpenInvoiceResponse[];
  currency: string;
}

type OpenInvoiceRow = billing_OpenInvoiceResponse & { id: string };

export function DunningOpenInvoicesTable({ invoices, currency }: Props) {
  const { i18n } = useLingui();

  const rows = useMemo<OpenInvoiceRow[]>(
    () =>
      invoices.map((inv, idx) => ({
        ...inv,
        id: inv.invoice_id ?? inv.invoice_number ?? `row-${idx}`,
      })),
    [invoices]
  );

  const columns: EditableCellConfig<OpenInvoiceRow>[] = useMemo(
    () => [
      {
        accessorKey: 'invoice_number',
        header: i18n._(msg({ id: 'dunning.drawer.openInvoices.column.invoiceNumber', message: 'Invoice' })),
        editable: false,
        sortable: false,
        cell: ({ row }: { row: { original: OpenInvoiceRow } }) => (
          <InvoiceLink
            invoiceId={row.original.invoice_id}
            invoiceNumber={row.original.invoice_number}
          />
        ),
      },
      {
        accessorKey: 'due_date',
        header: i18n._(msg({ id: 'dunning.drawer.openInvoices.column.dueDate', message: 'Due date' })),
        editable: false,
        sortable: false,
        cell: ({ row }: { row: { original: OpenInvoiceRow } }) => (
          <span>{row.original.due_date ? formatDateDisplay(row.original.due_date) : '—'}</span>
        ),
      },
      {
        accessorKey: 'original_amount_cents',
        header: i18n._(msg({ id: 'dunning.drawer.openInvoices.column.original', message: 'Original' })),
        editable: false,
        sortable: false,
        cell: ({ row }: { row: { original: OpenInvoiceRow } }) => (
          <span className="font-mono">
            {formatMoneyCents(row.original.original_amount_cents ?? 0, currency)}
          </span>
        ),
      },
      {
        accessorKey: 'outstanding_amount_cents',
        header: i18n._(msg({ id: 'dunning.drawer.openInvoices.column.outstanding', message: 'Outstanding' })),
        editable: false,
        sortable: false,
        cell: ({ row }: { row: { original: OpenInvoiceRow } }) => (
          <span className="font-mono">
            {formatMoneyCents(row.original.outstanding_amount_cents ?? 0, currency)}
          </span>
        ),
      },
      {
        accessorKey: 'days_overdue',
        header: i18n._(msg({ id: 'dunning.drawer.openInvoices.column.daysOverdue', message: 'Days overdue' })),
        editable: false,
        sortable: false,
      },
    ],
    [i18n, currency]
  );

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="dunning-open-invoices-empty">
        <Trans>No open invoices.</Trans>
      </p>
    );
  }

  return (
    <BaseEditableTable<OpenInvoiceRow>
      data={rows}
      columns={columns}
      tableId="dunning-open-invoices"
      enableSearch={false}
      // #1156: this was `enablePagination={false}`, a prop `BaseEditableTable` has
      // never declared or read — the drawer's short open-invoice list still rendered a
      // pagination footer. `hidePagination` is the real prop for that intent.
      hidePagination
      emptyMessage={i18n._(msg({ id: 'dunning.drawer.openInvoices.empty', message: 'No open invoices.' }))}
    />
  );
}
